
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Upload, UserPlus, Mail, Users, CheckCircle2, AlertCircle, Send, FileSpreadsheet, Info, Search, X, ChevronLeft, ChevronRight, ArrowUpDown, Calendar, ShieldCheck, Clock, Loader2, CheckCircle, XCircle, RefreshCw, Layers, Phone, Building2, BookOpen } from '../DeskeyIcons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FALLBACK = {
  400: 'Bad request.',
  401: 'Please sign in again to continue.',
  402: 'Not enough Super Credits for this action.',
  403: "You don't have access to this resource.",
  404: 'Not found.',
  409: 'Conflict — please refresh and try again.',
  413: 'That file is too large.',
  422: 'Some inputs are invalid.',
  429: 'Too many requests — please slow down.',
  500: 'Server error. Please try again in a moment.',
  502: 'Upstream service is busy. Please retry.',
  503: 'Service temporarily unavailable.',
  504: 'Request timed out. Please retry.',
};

function api(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { 'Authorization': `Bearer ${token}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  return fetch(`${API_URL}/api${path}`, { headers, ...opts }).then(async (res) => {
    let text = '';
    try {
      text = await res.clone().text();
    } catch {
      try { text = await res.text(); } catch { text = ''; }
    }

    let body = null;
    if (text) {
      try { body = JSON.parse(text); }
      catch { body = { _raw: text }; }
    }

    if (!res.ok) {
      const detail =
        (body && (body.detail || body.message || body._raw)) ||
        STATUS_FALLBACK[res.status] ||
        `Request failed (${res.status})`;
      const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      err.status = res.status;
      err.body = body;
      throw err;
    }
    return body;
  });
}

const TABS = [
  { id: 'licensed', label: 'Licensed Users', icon: ShieldCheck },
  { id: 'manual', label: 'Add Manually', icon: UserPlus },
  { id: 'csv', label: 'Import CSV', icon: FileSpreadsheet },
];

const PAGE_SIZE = 25;

// Small helper used by DPDP minor consent card
function ChannelBadge({ label, mocked }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${
      mocked ? 'bg-gray-100 text-gray-600 border border-gray-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    }`}>
      {label} {mocked ? '· mock' : '· sent'}
    </span>
  );
}

/* ── Licensed User Profile Modal ── */
function LicProfileModal({ person, accent, onClose }) {
  if (!person) return null;
  const A = accent;
  const initial = (person.full_name || '?')[0].toUpperCase();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '—';
  const isTeacher = person.role === 'teacher';

  const InfoRow = ({ icon: Icon, label, value, mono }) => (
    <div className="flex items-center gap-3 py-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${A}10` }}>
        <Icon className="w-4 h-4" style={{ color: A }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{label}</p>
        <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lic-profile-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="h-28 relative" style={{ background: `linear-gradient(135deg, ${A}, ${A}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="close-lic-profile-modal">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex justify-center -mt-14">
          <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-bold" style={{ background: A }}>
            {initial}
          </div>
        </div>
        <div className="text-center mt-3 px-6">
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{person.full_name}</h3>
          <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wider font-medium">{isTeacher ? 'Teacher' : 'Student'}</p>
          <div className="mt-1.5 flex items-center justify-center gap-2">
            <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border ${person.licence_status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
              {person.licence_status === 'active' ? 'Active' : 'Pending'}
            </span>
            {person.is_expired && <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold border bg-red-50 text-red-700 border-red-200">Expired</span>}
            {person.is_expiring_soon && !person.is_expired && <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold border bg-amber-50 text-amber-600 border-amber-200">Expiring Soon</span>}
          </div>
        </div>
        <div className="px-6 mt-5 space-y-0 divide-y divide-gray-100">
          <InfoRow icon={Mail} label="Email" value={person.email} />
          <InfoRow icon={Phone} label="Phone" value={person.phone || 'Not provided'} />
          <InfoRow icon={Calendar} label="Joined" value={formatDate(person.created_at)} />
          <InfoRow icon={Clock} label="Licence Duration" value={`${person.licence_duration_months || 12} months`} />
          {person.licence_expires_at && <InfoRow icon={Calendar} label="Expires" value={formatDate(person.licence_expires_at)} />}
          {person.days_remaining != null && <InfoRow icon={ShieldCheck} label="Days Remaining" value={`${person.days_remaining} days`} />}
        </div>
        {!isTeacher && (person.batches || []).length > 0 && (
          <div className="px-6 py-4">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Batches</p>
            <div className="flex flex-wrap gap-1.5">
              {person.batches.map(b => (
                <span key={b.id} className="text-xs px-3 py-1 rounded-lg font-medium bg-violet-50 text-violet-600 border border-violet-100">{b.name}</span>
              ))}
            </div>
          </div>
        )}
        <div className="px-6 pb-6 pt-2">
          <button onClick={onClose} className="w-full h-9 rounded-xl text-sm font-semibold text-gray-600 border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="lic-profile-close-btn">Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Slide-over Panel ── */
function SlidePanel({ open, onClose, title, subtitle, icon: Icon, count, children }) {
  if (!open) return null;
  return (
    <div className="fixed bottom-0 right-0 z-30 lg:left-[260px] left-0" style={{ top: 56 }} data-testid="slide-panel">
      <div className="absolute inset-0 bg-black/8 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 bottom-0 right-0 w-full max-w-3xl bg-white flex flex-col animate-in slide-in-from-right duration-200 shadow-[-4px_0_24px_rgba(0,0,0,0.06)] border-l border-gray-200">
        {/* Panel Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="slide-panel-close">
            <X className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {Icon && <Icon className="w-4 h-4 text-gray-500" />}
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
              <span className="text-[10px] bg-gray-100 text-gray-600 font-semibold px-2 py-0.5 rounded-full">{count}</span>
            </div>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── Recently Added Panel Content ── */
function RecentImportsPanel({ data, accent }) {
  const A = accent;
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  const filtered = useMemo(() => {
    let list = [...data];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        u.email?.toLowerCase().includes(q) ||
        u.full_name?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        u.licence_key?.toLowerCase().includes(q)
      );
    }
    if (roleFilter !== 'all') list = list.filter(u => u.role === roleFilter);
    if (methodFilter !== 'all') list = list.filter(u => u.added_method === methodFilter);
    if (dateFrom) {
      const from = new Date(dateFrom); from.setHours(0, 0, 0, 0);
      list = list.filter(u => u.created_at && new Date(u.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo); to.setHours(23, 59, 59, 999);
      list = list.filter(u => u.created_at && new Date(u.created_at) <= to);
    }
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'name') { va = a.full_name || ''; vb = b.full_name || ''; }
      else if (sortBy === 'email') { va = a.email || ''; vb = b.email || ''; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, search, roleFilter, methodFilter, dateFrom, dateTo, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [search, roleFilter, methodFilter, dateFrom, dateTo]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => (
    <ArrowUpDown className={`w-3 h-3 ml-1 inline ${sortBy === col ? 'text-gray-800' : 'text-gray-300'}`} />
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // Selection
  const allPageSelected = paged.length > 0 && paged.every(u => selected.has(u.id));
  const someSelected = selected.size > 0;
  const toggleRow = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAllPage = () => {
    if (allPageSelected) setSelected(prev => { const s = new Set(prev); paged.forEach(u => s.delete(u.id)); return s; });
    else setSelected(prev => { const s = new Set(prev); paged.forEach(u => s.add(u.id)); return s; });
  };

  // Active filters
  const hasFilters = search || roleFilter !== 'all' || methodFilter !== 'all' || dateFrom || dateTo;
  const clearAll = () => { setSearch(''); setRoleFilter('all'); setMethodFilter('all'); setDateFrom(''); setDateTo(''); };
  const filterTags = [];
  if (search) filterTags.push({ label: `"${search}"`, clear: () => setSearch('') });
  if (roleFilter !== 'all') filterTags.push({ label: roleFilter === 'student' ? 'Students' : 'Teachers', clear: () => setRoleFilter('all') });
  if (methodFilter !== 'all') filterTags.push({ label: methodFilter === 'manual' ? 'Manual' : 'CSV', clear: () => setMethodFilter('all') });
  if (dateFrom) filterTags.push({ label: `From ${dateFrom}`, clear: () => setDateFrom('') });
  if (dateTo) filterTags.push({ label: `To ${dateTo}`, clear: () => setDateTo('') });

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="px-5 py-3 border-b border-gray-100 space-y-2 flex-shrink-0" data-testid="imports-toolbar">
        {/* Single row: Search + Filters + Date */}
        <div className="flex items-center gap-1.5">
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, phone..."
              className="w-full h-8 pl-8 pr-2 text-[12px] rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white placeholder:text-gray-300"
              data-testid="imports-search"
            />
          </div>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="imports-role-filter">
            <option value="all">All Roles</option>
            <option value="student">Students</option>
            <option value="teacher">Teachers</option>
          </select>
          <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="imports-method-filter">
            <option value="all">All Methods</option>
            <option value="manual">Manual</option>
            <option value="csv_import">CSV</option>
          </select>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-1.5 bg-white text-gray-600 w-[110px]" title="From date" data-testid="imports-date-from" />
          <span className="text-[10px] text-gray-300">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-1.5 bg-white text-gray-600 w-[110px]" title="To date" data-testid="imports-date-to" />
        </div>

        {/* Filter tags (only when active) */}
        {hasFilters && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="imports-active-filters">
            {filterTags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 pl-2 pr-0.5 py-[3px] rounded-full">
                {tag.label}
                <button onClick={tag.clear} className="w-3.5 h-3.5 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors" data-testid={`imports-clear-tag-${i}`}>
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <button onClick={clearAll} className="text-[10px] text-gray-500 hover:text-red-500 font-medium transition-colors" data-testid="imports-clear-all">
              Clear all
            </button>
            <span className="text-[10px] text-gray-500 ml-auto">{filtered.length} of {data.length}</span>
          </div>
        )}
      </div>

      {/* Selection bar */}
      {someSelected && (
        <div className="px-5 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-3 flex-shrink-0" data-testid="imports-selection-bar">
          <span className="text-xs font-semibold text-gray-800">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-[11px] text-gray-500 hover:text-gray-700 font-medium transition-colors" data-testid="imports-clear-selection">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm" data-testid="imports-table">
          <thead className="sticky top-0 bg-gray-50/95 backdrop-blur-sm">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 cursor-pointer" data-testid="imports-select-all" />
              </th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('name')}>Name <SortIcon col="name" /></th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('email')}>Email <SortIcon col="email" /></th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Method</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Licence</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('date')}>Added <SortIcon col="date" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {paged.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-16 text-gray-500 text-sm">No users match your filters</td></tr>
            ) : paged.map(u => {
              const isChecked = selected.has(u.id);
              return (
                <tr key={u.id} className={`transition-colors ${isChecked ? 'bg-gray-50' : 'hover:bg-gray-50/60'}`} data-testid={`import-row-${u.id}`}>
                  <td className="w-10 px-3 py-3">
                    <input type="checkbox" checked={isChecked} onChange={() => toggleRow(u.id)} className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 cursor-pointer" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: `${A}cc` }}>
                        {(u.full_name || '?')[0].toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{u.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[180px]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${u.role === 'teacher' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[10px] text-gray-600 font-medium">{u.added_method === 'csv_import' ? 'CSV' : 'Manual'}</td>
                  <td className="px-4 py-3 text-[10px] text-gray-500">{u.licence_duration_months || 12}mo</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(u.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-testid="imports-pagination">
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sent Invites Panel Content ── */
function InvitesPanel({ data }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkFilter, setLinkFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  const now = new Date();
  const isExpired = (inv) => inv.expires_at && new Date(inv.expires_at) < now;
  const isActive = (inv) => inv.expires_at && new Date(inv.expires_at) >= now;

  const filtered = useMemo(() => {
    let list = [...data];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(inv =>
        inv.email?.toLowerCase().includes(q) ||
        inv.full_name?.toLowerCase().includes(q) ||
        inv.role?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') list = list.filter(inv => inv.status === statusFilter);
    if (linkFilter === 'active') list = list.filter(inv => isActive(inv));
    else if (linkFilter === 'expired') list = list.filter(inv => isExpired(inv));
    if (dateFrom) {
      const from = new Date(dateFrom); from.setHours(0, 0, 0, 0);
      list = list.filter(inv => inv.created_at && new Date(inv.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo); to.setHours(23, 59, 59, 999);
      list = list.filter(inv => inv.created_at && new Date(inv.created_at) <= to);
    }
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'email') { va = a.email || ''; vb = b.email || ''; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, search, statusFilter, linkFilter, dateFrom, dateTo, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [search, statusFilter, linkFilter, dateFrom, dateTo]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => (
    <ArrowUpDown className={`w-3 h-3 ml-1 inline ${sortBy === col ? 'text-gray-800' : 'text-gray-300'}`} />
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const statusColors = { pending: 'bg-amber-50 text-amber-600 border-amber-100', accepted: 'bg-emerald-50 text-emerald-600 border-emerald-100' };

  // Summary counts
  const activeCount = data.filter(inv => isActive(inv)).length;
  const expiredCount = data.filter(inv => isExpired(inv)).length;

  // Selection
  const allPageSelected = paged.length > 0 && paged.every(inv => selected.has(inv.id));
  const someSelected = selected.size > 0;
  const toggleRow = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAllPage = () => {
    if (allPageSelected) setSelected(prev => { const s = new Set(prev); paged.forEach(inv => s.delete(inv.id)); return s; });
    else setSelected(prev => { const s = new Set(prev); paged.forEach(inv => s.add(inv.id)); return s; });
  };

  // Active filters
  const hasFilters = search || statusFilter !== 'all' || linkFilter !== 'all' || dateFrom || dateTo;
  const clearAll = () => { setSearch(''); setStatusFilter('all'); setLinkFilter('all'); setDateFrom(''); setDateTo(''); };
  const filterTags = [];
  if (search) filterTags.push({ label: `"${search}"`, clear: () => setSearch('') });
  if (statusFilter !== 'all') filterTags.push({ label: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1), clear: () => setStatusFilter('all') });
  if (linkFilter !== 'all') filterTags.push({ label: linkFilter === 'active' ? 'Active links' : 'Expired links', clear: () => setLinkFilter('all') });
  if (dateFrom) filterTags.push({ label: `From ${dateFrom}`, clear: () => setDateFrom('') });
  if (dateTo) filterTags.push({ label: `To ${dateTo}`, clear: () => setDateTo('') });

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="px-5 py-3 border-b border-gray-100 space-y-2 flex-shrink-0" data-testid="invites-toolbar">
        {/* Single row: Search + Filters + Date */}
        <div className="flex items-center gap-1.5">
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search email, name, role..."
              className="w-full h-8 pl-8 pr-2 text-[12px] rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white placeholder:text-gray-300"
              data-testid="invites-search"
            />
          </div>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="invites-status-filter">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
          </select>
          <select value={linkFilter} onChange={e => setLinkFilter(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="invites-link-filter">
            <option value="all">All Links</option>
            <option value="active">Active ({activeCount})</option>
            <option value="expired">Expired ({expiredCount})</option>
          </select>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-1.5 bg-white text-gray-600 w-[110px]" title="From date" data-testid="invites-date-from" />
          <span className="text-[10px] text-gray-300">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-1.5 bg-white text-gray-600 w-[110px]" title="To date" data-testid="invites-date-to" />
        </div>

        {/* Filter tags (only when active) */}
        {hasFilters && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="invites-active-filters">
            {filterTags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 pl-2 pr-0.5 py-[3px] rounded-full">
                {tag.label}
                <button onClick={tag.clear} className="w-3.5 h-3.5 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors" data-testid={`invites-clear-tag-${i}`}>
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <button onClick={clearAll} className="text-[10px] text-gray-500 hover:text-red-500 font-medium transition-colors" data-testid="invites-clear-all">
              Clear all
            </button>
            <span className="text-[10px] text-gray-500 ml-auto">{filtered.length} of {data.length}</span>
          </div>
        )}
      </div>

      {/* Selection bar */}
      {someSelected && (
        <div className="px-5 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-3 flex-shrink-0" data-testid="invites-selection-bar">
          <span className="text-xs font-semibold text-gray-800">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-[11px] text-gray-500 hover:text-gray-700 font-medium transition-colors" data-testid="invites-clear-selection">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm" data-testid="invites-table">
          <thead className="sticky top-0 bg-gray-50/95 backdrop-blur-sm">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 cursor-pointer" data-testid="invites-select-all" />
              </th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('email')}>Email <SortIcon col="email" /></th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Link</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('date')}>Sent <SortIcon col="date" /></th>
              <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Expires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {paged.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-16 text-gray-500 text-sm">No invites match your filters</td></tr>
            ) : paged.map(inv => {
              const expired = isExpired(inv);
              const isChecked = selected.has(inv.id);
              return (
                <tr key={inv.id} className={`transition-colors ${isChecked ? 'bg-gray-50' : 'hover:bg-gray-50/60'}`} data-testid={`invite-row-${inv.id}`}>
                  <td className="w-10 px-3 py-3">
                    <input type="checkbox" checked={isChecked} onChange={() => toggleRow(inv.id)} className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 cursor-pointer" />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate max-w-[180px]">{inv.email}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[120px]">{inv.full_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${inv.role === 'teacher' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                      {inv.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border capitalize ${statusColors[inv.status] || statusColors.pending}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${expired ? 'bg-red-50 text-red-500 border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                      {expired ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(inv.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(inv.expires_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-testid="invites-pagination">
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export function LicensingSection({ accent, departments }) {
  const A = accent || '#34A853';

  // ─── Main state ───
  const [activeTab, setActiveTab] = useState('licensed');
  const [billingGate, setBillingGate] = useState(null); // { billing_model, plan_payment_status, plan_name }
  const [recentImports, setRecentImports] = useState([]);
  const [invites, setInvites] = useState([]);
  const [licensedUsers, setLicensedUsers] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLicUser, setSelectedLicUser] = useState(null);

  // Panel state
  const [importsPanel, setImportsPanel] = useState(false);
  const [invitesPanel, setInvitesPanel] = useState(false);

  // Manual add form
  const [manualForm, setManualForm] = useState({
    full_name: '', email: '', role: 'student', phone: '', department_id: '', licence_duration_months: 12,
    // DPDP — student minor consent
    dob: '',
    parent_name: '', parent_email: '', parent_mobile: '', parent_whatsapp: '', parent_relationship: 'parent',
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // CSV import
  const [csvText, setCsvText] = useState('');       // contents of a dropped/selected .csv
  const [xlsxB64, setXlsxB64] = useState('');       // base64 of a dropped/selected .xlsx
  const [csvFileName, setCsvFileName] = useState('');
  const [csvDragOver, setCsvDragOver] = useState(false);
  const [csvRole, setCsvRole] = useState('student');
  const [csvDuration, setCsvDuration] = useState(12);
  const [csvSubmitting, setCsvSubmitting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Email invite → replaced with Add Member
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberName, setAddMemberName] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('student');
  const [addMemberDept, setAddMemberDept] = useState('');
  const [addMemberDuration, setAddMemberDuration] = useState(12);
  const [addMemberSubmitting, setAddMemberSubmitting] = useState(false);
  const [addMemberResult, setAddMemberResult] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);

  // Licensed users - extend
  const [licSearch, setLicSearch] = useState('');
  const [licRole, setLicRole] = useState('all');
  const [licExpiry, setLicExpiry] = useState('all');
  const [licBatch, setLicBatch] = useState('all');
  const [licSort, setLicSort] = useState('days');
  const [licDir, setLicDir] = useState('asc');
  const [licPage, setLicPage] = useState(1);
  const [licSelected, setLicSelected] = useState(new Set());
  const [extendMonths, setExtendMonths] = useState(6);
  const [extending, setExtending] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [imports, inv, lic, jr] = await Promise.all([
        api('/institution/licensing/recent-imports'),
        api('/institution/licensing/invites'),
        api('/institution/licensing/licensed-users'),
        api('/institution/licensing/join-requests'),
      ]);
      setRecentImports(imports.users || []);
      setInvites(inv.invites || []);
      setLicensedUsers(lic.users || []);
      setAllBatches(lic.batches || []);
      setJoinRequests(jr.join_requests || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    api('/institution/invoices/summary').then((s) => setBillingGate({
      billing_model: s.billing_model, plan_payment_status: s.plan_payment_status, plan_name: s.plan_name,
    })).catch(() => {});
  }, []);

  const planUnpaid = billingGate?.billing_model === 'plan' && billingGate?.plan_payment_status !== 'paid';

  // ─── JOIN REQUEST HANDLERS ───
  const handleApproveJoinRequest = async (requestId, source = 'admin_invite') => {
    try {
      if (source === 'self_signup') {
        // For self-signup licence requests
        await api('/institution/approve', {
          method: 'POST',
          body: JSON.stringify({ request_id: requestId, action: 'approve' })
        });
      } else {
        // For admin-invited join requests
        await api(`/institution/licensing/join-request/${requestId}/approve`, { method: 'POST' });
      }
      toast.success('Join request approved');
      loadData(); // Refresh the list
    } catch (e) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectJoinRequest = async (requestId, source = 'admin_invite') => {
    try {
      if (source === 'self_signup') {
        // For self-signup licence requests
        await api('/institution/approve', {
          method: 'POST',
          body: JSON.stringify({ request_id: requestId, action: 'reject' })
        });
      } else {
        // For admin-invited join requests
        await api(`/institution/licensing/join-request/${requestId}/reject`, { method: 'POST' });
      }
      toast.success('Join request rejected');
      loadData(); // Refresh the list
    } catch (e) {
      toast.error('Failed to reject request');
    }
  };

  // ─── MANUAL ADD (Smart: creates user or sends invite/join request) ───
  const [manualResult, setManualResult] = useState(null);

  // ── DPDP — Detect under-18 students ──
  const computeAge = (dob) => {
    if (!dob) return null;
    try {
      const d = new Date(dob);
      if (isNaN(d.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
      return age;
    } catch { return null; }
  };
  const studentAge = computeAge(manualForm.dob);
  const isMinorPath = manualForm.role === 'student' && studentAge !== null && studentAge < 18;

  const handleManualAdd = async (e) => {
    e.preventDefault();
    if (!manualForm.full_name.trim() || !manualForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    // DPDP minor route — backend handles parent invite + consent gate
    if (isMinorPath) {
      const parentMobile = (manualForm.parent_mobile || '').replace(/\D/g, '').slice(-10);
      if (!manualForm.parent_name.trim() || !manualForm.parent_email.trim() || parentMobile.length !== 10) {
        toast.error('Parent name, email and 10-digit mobile are required for under-18 students (DPDP)');
        return;
      }
      setManualSubmitting(true);
      setManualResult(null);
      try {
        const res = await api('/institution/students/invite-minor', {
          method: 'POST',
          body: JSON.stringify({
            full_name: manualForm.full_name.trim(),
            email: manualForm.email.trim().toLowerCase(),
            role: 'student',
            phone: manualForm.phone || null,
            department_id: manualForm.department_id || null,
            licence_duration_months: manualForm.licence_duration_months,
            dob: manualForm.dob,
            parent: {
              full_name: manualForm.parent_name.trim(),
              email: manualForm.parent_email.trim().toLowerCase(),
              mobile: parentMobile,
              whatsapp: (manualForm.parent_whatsapp || '').replace(/\D/g, '').slice(-10) || parentMobile,
              relationship: manualForm.parent_relationship || 'parent',
            },
          }),
        });
        setManualResult(res);
        if (res.status === 'consent_invited') {
          toast.success('Parent consent link dispatched');
        } else if (res.status === 'already_member') {
          toast.info(res.message);
        } else if (res.status === 'already_pending') {
          toast.info(res.message);
        } else if (res.status === 'adult_account_created') {
          toast.success('Adult — account created directly');
        } else {
          toast.error(res.detail || res.message || 'Unexpected response');
        }
        if (res.status === 'consent_invited' || res.status === 'adult_account_created') {
          setManualForm({ full_name: '', email: '', role: 'student', phone: '', department_id: '', licence_duration_months: 12,
                          dob: '', parent_name: '', parent_email: '', parent_mobile: '', parent_whatsapp: '', parent_relationship: 'parent' });
          loadData();
        }
      } catch {
        toast.error('Failed to start consent flow');
      }
      setManualSubmitting(false);
      return;
    }

    setManualSubmitting(true);
    setManualResult(null);
    try {
      const res = await api('/institution/licensing/add-manual', {
        method: 'POST',
        body: JSON.stringify({
          full_name: manualForm.full_name, email: manualForm.email, role: manualForm.role,
          phone: manualForm.phone, department_id: manualForm.department_id,
          // Persist the admin-entered DOB (students) so onboarding won't re-ask it.
          dob: manualForm.role === 'student' ? (manualForm.dob || null) : null,
        }),
      });
      if (res.status === 'user_created') {
        toast.success(`${manualForm.full_name} added with licence. Default password: ${res.default_password}`);
        setManualResult({ status: 'user_created', message: res.message, default_password: res.default_password, email_sent: res.email_sent });
        setManualForm({ full_name: '', email: '', role: 'student', phone: '', department_id: '',
                        dob: '', parent_name: '', parent_email: '', parent_mobile: '', parent_whatsapp: '', parent_relationship: 'parent' });
        loadData();
      } else if (res.status === 'join_request_sent') {
        toast.success('Join request sent!');
        setManualResult({ status: 'join_request_sent', message: res.message });
        loadData();
      } else if (res.status === 'already_member') {
        toast.info(res.message);
        setManualResult({ status: 'already_member', message: res.message });
      } else if (res.status === 'already_pending') {
        toast.info(res.message);
        setManualResult({ status: 'already_pending', message: res.message });
      } else {
        toast.error(res.detail || res.message || 'Failed to add user');
      }
    } catch {
      toast.error('Failed to add user');
    }
    setManualSubmitting(false);
  };

  // ─── CSV IMPORT ───
  const CSV_MAX_BYTES = 5 * 1024 * 1024;
  const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const toBase64 = (buf) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    return btoa(bin);
  };
  const loadCsvFile = async (file) => {
    if (!file) return;
    const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
    const isXlsx = /\.xlsx$/i.test(file.name) || file.type === XLSX_MIME;
    if (/\.xls$/i.test(file.name)) {
      toast.error('Old .xls files are not supported — save it as .xlsx or .csv');
      return;
    }
    if (!isCsv && !isXlsx) {
      toast.error('Please choose a .csv or .xlsx file');
      return;
    }
    if (file.size > CSV_MAX_BYTES) {
      toast.error('File is too large (max 5 MB)');
      return;
    }
    try {
      if (isXlsx) { setXlsxB64(toBase64(await file.arrayBuffer())); setCsvText(''); }
      else { setCsvText(await file.text()); setXlsxB64(''); }
      setCsvFileName(file.name);
      setCsvResult(null);
    } catch {
      toast.error('Could not read that file');
    }
  };
  const clearCsvFile = () => { setCsvText(''); setXlsxB64(''); setCsvFileName(''); setCsvResult(null); };
  const hasImportFile = Boolean(xlsxB64 || csvText.trim());

  // Save the server's result file — same format as uploaded (.xlsx or .csv),
  // with the password + import_status columns added.
  const downloadCsvResult = () => {
    const base = (csvFileName || 'users').replace(/\.(csv|xlsx)$/i, '');
    let blob;
    let ext;
    if (csvResult?.result_xlsx_base64) {
      const bin = atob(csvResult.result_xlsx_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      blob = new Blob([bytes], { type: XLSX_MIME });
      ext = 'xlsx';
    } else if (csvResult?.result_csv) {
      blob = new Blob([csvResult.result_csv], { type: 'text/csv;charset=utf-8' });
      ext = 'csv';
    } else {
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}-with-passwords.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCSVImport = async () => {
    if (!hasImportFile) {
      toast.error('Drop a CSV or Excel file first');
      return;
    }
    setCsvSubmitting(true);
    setCsvResult(null);
    try {
      const res = await api('/institution/licensing/import-csv', {
        method: 'POST',
        body: JSON.stringify(xlsxB64
          ? { xlsx_base64: xlsxB64, role: csvRole, licence_duration_months: csvDuration }
          : { csv_text: csvText, role: csvRole, licence_duration_months: csvDuration }),
      });
      setCsvResult(res);
      if (res.added?.length > 0) {
        toast.success(`${res.added.length} user(s) imported`);
        loadData();
      } else {
        toast.info('No new users were imported');
      }
    } catch {
      toast.error('Import failed');
    }
    setCsvSubmitting(false);
  };

  // ─── ADD MEMBER (Smart flow) ───
  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addMemberEmail.trim()) { toast.error('Email is required'); return; }
    setAddMemberSubmitting(true);
    setAddMemberResult(null);
    try {
      const res = await api('/institution/licensing/add-member', {
        method: 'POST',
        body: JSON.stringify({
          email: addMemberEmail.trim().toLowerCase(),
          full_name: addMemberName.trim() || null,
          role: addMemberRole,
          licence_duration_months: addMemberDuration,
          department_id: addMemberDept || null,
        }),
      });
      setAddMemberResult(res);
      if (res.status === 'already_member') toast.info(res.message);
      else if (res.status === 'join_request_sent') toast.success(res.message);
      else if (res.status === 'already_pending') toast.info(res.message);
      else if (res.status === 'invite_sent') {
        toast.success(res.message);
        if (res.dev_passcode) toast.info(`Dev Passcode: ${res.dev_passcode}`, { duration: 8000 });
      }
      else toast.error(res.detail || 'Unexpected response');
      if (res.status === 'join_request_sent' || res.status === 'invite_sent') {
        setAddMemberEmail(''); setAddMemberName(''); setAddMemberRole('student'); setAddMemberDept(''); setAddMemberDuration(12);
        loadData();
      }
    } catch { toast.error('Failed to add member'); }
    setAddMemberSubmitting(false);
  };

  // (2FA toggle per user removed — 2FA is now a page-level gate)

  const deptList = departments || [];

  // ─── EXTEND LICENCE ───
  const handleExtend = async () => {
    if (licSelected.size === 0) { toast.error('Select users first'); return; }
    setExtending(true);
    try {
      const res = await api('/institution/licensing/extend-licence', {
        method: 'POST', body: JSON.stringify({ user_ids: [...licSelected], extra_months: extendMonths })
      });
      if (res.updated > 0) { toast.success(res.message); setLicSelected(new Set()); loadData(); }
      else toast.error('No users updated');
    } catch { toast.error('Failed to extend'); }
    setExtending(false);
  };

  // ─── EXTRA LICENCE: DELETE (within 10-day window) / REASSIGN (after) ───
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignForm, setReassignForm] = useState({ full_name: '', email: '' });
  const [reassigning, setReassigning] = useState(false);

  const handleDeleteExtra = async (user) => {
    if (!window.confirm(`Delete the extra licence for ${user.full_name}? This frees the seat and drops its overage charge.`)) return;
    try {
      await api('/institution/revoke-licence', { method: 'POST', body: JSON.stringify({ user_id: user.id }) });
      toast.success('Extra licence deleted');
      loadData();
    } catch (e) { toast.error(e.message || 'Failed to delete licence'); }
  };

  const handleReassignExtra = async () => {
    if (!reassignForm.full_name.trim() || !reassignForm.email.trim()) { toast.error('Name and email are required'); return; }
    setReassigning(true);
    try {
      await api('/institution/licensing/reassign-extra', {
        method: 'POST',
        body: JSON.stringify({ old_user_id: reassignTarget.id, new_full_name: reassignForm.full_name, new_email: reassignForm.email }),
      });
      toast.success('Licence reassigned');
      setReassignTarget(null);
      setReassignForm({ full_name: '', email: '' });
      loadData();
    } catch (e) { toast.error(e.message || 'Failed to reassign licence'); }
    setReassigning(false);
  };

  // ─── SELECT ALL IN BATCH ───
  const handleSelectBatchUsers = () => {
    if (licBatch === 'all') return;
    const batchUserIds = filteredLic
      .filter(u => (u.batches || []).some(b => b.id === licBatch))
      .map(u => u.id);
    setLicSelected(new Set(batchUserIds));
    toast.success(`Selected ${batchUserIds.length} user(s) in this batch`);
  };

  // ─── LICENSED USERS FILTERING ───
  const filteredLic = useMemo(() => {
    let list = [...licensedUsers];
    if (licSearch) {
      const q = licSearch.toLowerCase();
      list = list.filter(u => u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }
    if (licRole !== 'all') list = list.filter(u => u.role === licRole);
    if (licExpiry === 'expiring') list = list.filter(u => u.is_expiring_soon);
    else if (licExpiry === 'expired') list = list.filter(u => u.is_expired);
    else if (licExpiry === 'active') list = list.filter(u => !u.is_expired && !u.is_expiring_soon);
    // Batch filter only applies to students
    if (licBatch !== 'all') list = list.filter(u => u.role === 'student' && (u.batches || []).some(b => b.id === licBatch));
    list.sort((a, b) => {
      let va, vb;
      if (licSort === 'name') { va = a.full_name || ''; vb = b.full_name || ''; }
      else if (licSort === 'email') { va = a.email || ''; vb = b.email || ''; }
      else { va = a.days_remaining ?? 9999; vb = b.days_remaining ?? 9999; }
      if (va < vb) return licDir === 'asc' ? -1 : 1;
      if (va > vb) return licDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [licensedUsers, licSearch, licRole, licExpiry, licBatch, licSort, licDir]);

  const licTotalPages = Math.max(1, Math.ceil(filteredLic.length / PAGE_SIZE));
  const licPaged = filteredLic.slice((licPage - 1) * PAGE_SIZE, licPage * PAGE_SIZE);
  useEffect(() => setLicPage(1), [licSearch, licRole, licExpiry, licBatch]);

  // Auto-clear batch filter when switching to teachers
  useEffect(() => { if (licRole === 'teacher') setLicBatch('all'); }, [licRole]);

  const licAllPageSelected = licPaged.length > 0 && licPaged.every(u => licSelected.has(u.id));
  const toggleLicRow = (id) => setLicSelected(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleLicAll = () => {
    if (licAllPageSelected) setLicSelected(p => { const s = new Set(p); licPaged.forEach(u => s.delete(u.id)); return s; });
    else setLicSelected(p => { const s = new Set(p); licPaged.forEach(u => s.add(u.id)); return s; });
  };
  const toggleLicSort = (col) => {
    if (licSort === col) setLicDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setLicSort(col); setLicDir('asc'); }
  };

  const expiringCount = licensedUsers.filter(u => u.is_expiring_soon).length;
  const expiredCount = licensedUsers.filter(u => u.is_expired).length;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // Summary counts
  const studentImports = recentImports.filter(u => u.role === 'student').length;
  const teacherImports = recentImports.filter(u => u.role === 'teacher').length;
  const pendingInvites = invites.filter(i => i.status === 'pending').length;
  const pendingJoinReqs = joinRequests.filter(r => r.status === 'pending').length;

  // Show batch filter only when not filtering by teachers
  const showBatchFilter = licRole !== 'teacher' && allBatches.length > 0;

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-120px)]" data-testid="licensing-section">

      <>
      {selectedLicUser && <LicProfileModal person={selectedLicUser} accent={A} onClose={() => setSelectedLicUser(null)} />}

      {planUnpaid && (
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="plan-unpaid-banner">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>Complete your {billingGate.plan_name} plan payment</strong> in Invoices → Payments before adding licences.
          </p>
        </div>
      )}

      {/* ─── SUMMARY STAT CARDS (TOP — fixed height) ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0" data-testid="licensing-stat-cards">
        {[
          { label: 'Licensed Users', val: licensedUsers.length, icon: ShieldCheck, color: A, sub: `${licensedUsers.filter(u => u.role === 'student').length} students, ${licensedUsers.filter(u => u.role === 'teacher').length} teachers` },
          { label: 'Expiring Soon', val: expiringCount, icon: Clock, color: '#f59e0b', sub: `${expiredCount} already expired` },
          {
            label: 'Recently Added', val: recentImports.length, icon: Users, color: '#6366f1',
            sub: `${studentImports} students, ${teacherImports} teachers`,
            onClick: () => setImportsPanel(true),
          },
          {
            label: 'Join Requests', val: joinRequests.length, icon: Send, color: '#ec4899',
            sub: `${pendingJoinReqs} pending`,
            onClick: () => setInvitesPanel(true),
          },
        ].map(s => {
          const Wrapper = s.onClick ? 'button' : 'div';
          return (
            <Wrapper
              key={s.label}
              onClick={s.onClick}
              className={`bg-white border border-gray-200 rounded-xl shadow p-4 h-[120px] flex flex-col justify-between transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 group text-left w-full ${s.onClick ? 'cursor-pointer' : ''}`}
              data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105" style={{ background: `${s.color}14` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.75} />
                </div>
                <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-500">{s.label}</span>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-gray-900 tabular-nums tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                <p className="text-[11px] text-gray-500 mt-0.5 truncate">{s.sub}</p>
              </div>
            </Wrapper>
          );
        })}
      </div>

      {/* Tab selector */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-shrink-0" data-testid="licensing-tabs">
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${active ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid={`licensing-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" strokeWidth={active ? 2 : 1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── LICENSED USERS ─── */}
      {activeTab === 'licensed' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden flex-1 min-h-0 flex flex-col" data-testid="licensed-users-section">
          {/* Toolbar */}
          <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="relative min-w-[180px] flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <input value={licSearch} onChange={e => setLicSearch(e.target.value)} placeholder="Search name or email..." className="w-full h-8 pl-8 pr-2 text-[12px] rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white placeholder:text-gray-300" data-testid="lic-search" />
              </div>
              <div className="w-px h-5 bg-gray-200 mx-0.5" />
              <select value={licRole} onChange={e => setLicRole(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="lic-role-filter">
                <option value="all">All Roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
              <select value={licExpiry} onChange={e => setLicExpiry(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="lic-expiry-filter">
                <option value="all">All Validity</option>
                <option value="active">Active</option>
                <option value="expiring">{`Expiring Soon (${expiringCount})`}</option>
                <option value="expired">{`Expired (${expiredCount})`}</option>
              </select>
              {showBatchFilter && (
                <>
                  <div className="w-px h-5 bg-gray-200 mx-0.5" />
                  <select value={licBatch} onChange={e => setLicBatch(e.target.value)} className="h-8 text-[11px] rounded-md border border-gray-200 px-2 bg-white text-gray-700" data-testid="lic-batch-filter">
                    <option value="all">All Batches</option>
                    {allBatches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  {licBatch !== 'all' && (
                    <Button
                      onClick={handleSelectBatchUsers}
                      variant="outline"
                      className="h-8 px-3 text-[11px] font-semibold border-gray-200 rounded-md"
                      data-testid="lic-select-batch-btn"
                    >
                      <Layers className="w-3 h-3 mr-1" />
                      Select All in Batch
                    </Button>
                  )}
                </>
              )}
              <span className="text-[10px] text-gray-500 ml-auto">{filteredLic.length} of {licensedUsers.length}</span>
            </div>
          </div>

          {/* Selection bar with extend */}
          {licSelected.size > 0 && (
            <div className="px-5 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-3 flex-shrink-0" data-testid="lic-selection-bar">
              <span className="text-xs font-semibold text-gray-800">{licSelected.size} selected</span>
              <div className="w-px h-4 bg-gray-200" />
              <span className="text-[11px] text-gray-600">Extend until institution renewal</span>
              <Button onClick={handleExtend} disabled={extending} className="h-7 px-3 rounded-md text-white text-[11px] font-semibold" style={{ background: A }} data-testid="lic-extend-btn">
                {extending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Extend
              </Button>
              <button onClick={() => setLicSelected(new Set())} className="text-[11px] text-gray-500 hover:text-gray-700 font-medium ml-auto" data-testid="lic-clear-selection">Clear</button>
            </div>
          )}

          {/* Table — fill remaining space */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full text-sm" data-testid="lic-table">
              <thead className="sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10">
                <tr>
                  <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={licAllPageSelected} onChange={toggleLicAll} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" data-testid="lic-select-all" /></th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleLicSort('name')}>Name <ArrowUpDown className={`w-3 h-3 ml-1 inline ${licSort === 'name' ? 'text-gray-800' : 'text-gray-300'}`} /></th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleLicSort('email')}>Email <ArrowUpDown className={`w-3 h-3 ml-1 inline ${licSort === 'email' ? 'text-gray-800' : 'text-gray-300'}`} /></th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Batch</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleLicSort('days')}>Validity <ArrowUpDown className={`w-3 h-3 ml-1 inline ${licSort === 'days' ? 'text-gray-800' : 'text-gray-300'}`} /></th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">Expires</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider w-14"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {licPaged.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16 text-gray-500 text-sm">No licensed users found</td></tr>
                ) : licPaged.map(u => {
                  const checked = licSelected.has(u.id);
                  const days = u.days_remaining;
                  let validityColor = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                  let validityLabel = days != null ? `${days}d` : '—';
                  if (u.is_expired) { validityColor = 'bg-red-50 text-red-500 border-red-100'; validityLabel = 'Expired'; }
                  else if (u.is_expiring_soon) { validityColor = 'bg-amber-50 text-amber-600 border-amber-100'; }
                  const isTeacher = u.role === 'teacher';
                  return (
                    <tr key={u.id} className={`transition-colors ${checked ? 'bg-gray-50' : 'hover:bg-gray-50/60'}`} data-testid={`lic-row-${u.id}`}>
                      <td className="w-10 px-3 py-2.5"><input type="checkbox" checked={checked} onChange={() => toggleLicRow(u.id)} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0" style={{ background: `${A}cc` }}>{(u.full_name || '?')[0].toUpperCase()}</div>
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{u.full_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600 truncate max-w-[180px]">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${isTeacher ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>{u.role}</span>
                          {u.is_extra_licence && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-amber-50 text-amber-600 border-amber-100" title="Beyond plan limit">Extra</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {isTeacher ? (
                          <span className="text-[10px] text-gray-300">—</span>
                        ) : (u.batches || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.batches.map(b => (
                              <span key={b.id} className="text-[10px] px-2 py-0.5 rounded-md font-medium bg-violet-50 text-violet-600 border border-violet-100 truncate max-w-[120px]">{b.name}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-300 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${validityColor}`}>{validityLabel}</span></td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(u.licence_expires_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.is_extra_licence && (
                            u.extra_licence_window_open ? (
                              <button onClick={() => handleDeleteExtra(u)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50 transition-colors group" data-testid={`lic-delete-extra-${u.id}`} title="Delete extra licence (within 10 days)">
                                <XCircle className="w-4 h-4 text-red-400 group-hover:text-red-600 transition-colors" />
                              </button>
                            ) : (
                              <button onClick={() => { setReassignTarget(u); setReassignForm({ full_name: '', email: '' }); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-amber-50 transition-colors group" data-testid={`lic-reassign-extra-${u.id}`} title="Reassign extra licence">
                                <RefreshCw className="w-4 h-4 text-amber-400 group-hover:text-amber-600 transition-colors" />
                              </button>
                            )
                          )}
                          <button onClick={() => setSelectedLicUser(u)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors group" data-testid={`lic-info-${u.id}`} title="View profile">
                            <Info className="w-4 h-4 text-gray-500 group-hover:text-gray-800 transition-colors" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {licTotalPages > 1 && (
            <div className="px-5 py-2.5 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-gray-500">Page {licPage} of {licTotalPages}</span>
              <div className="flex gap-1">
                <button disabled={licPage <= 1} onClick={() => setLicPage(p => p - 1)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4 text-gray-600" /></button>
                <button disabled={licPage >= licTotalPages} onClick={() => setLicPage(p => p + 1)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4 text-gray-600" /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── MANUAL ADD ─── */}
      {activeTab === 'manual' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow flex flex-col overflow-hidden h-full" data-testid="manual-add-form">
          <div className="p-6 flex-shrink-0">
            <div className="mb-5">
              <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Add User</h3>
              <p className="text-sm text-gray-500 mt-0.5">Add a new or existing user. New users get an account with licence; existing users receive a join invite.</p>
            </div>
          </div>

          <form onSubmit={handleManualAdd} className="space-y-4 max-w-2xl px-6 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Full Name *</label>
                <Input value={manualForm.full_name} onChange={e => setManualForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Aarav Sharma" className="h-10 rounded-lg text-sm" data-testid="manual-name" required />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Email *</label>
                <Input type="email" value={manualForm.email} onChange={e => setManualForm(p => ({ ...p, email: e.target.value }))} placeholder="aarav@example.com" className="h-10 rounded-lg text-sm" data-testid="manual-email" required />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Role</label>
                <select value={manualForm.role} onChange={e => setManualForm(p => ({ ...p, role: e.target.value }))} className="w-full h-10 rounded-lg border border-gray-200 text-sm px-3 bg-white text-gray-700" data-testid="manual-role">
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Phone</label>
                <Input value={manualForm.phone} onChange={e => setManualForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="98765 43210" className="h-10 rounded-lg text-sm" data-testid="manual-phone" maxLength={10} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  Licence
                  <span className="ml-1 normal-case font-normal text-[10px] text-gray-400">(co-terminus with institution)</span>
                </label>
                <div className="h-10 rounded-lg border border-gray-200 bg-gray-50 text-sm px-3 flex items-center text-gray-600 font-medium" data-testid="manual-duration-locked">
                  Until institution renewal
                </div>
              </div>
            </div>

            {deptList.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Department</label>
                <select value={manualForm.department_id} onChange={e => setManualForm(p => ({ ...p, department_id: e.target.value }))} className="w-full h-10 rounded-lg border border-gray-200 text-sm px-3 bg-white text-gray-700" data-testid="manual-department">
                  <option value="">No department</option>
                  {deptList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}

            {/* ── DPDP — Date of Birth (only for students) ── */}
            {manualForm.role === 'student' && (
              <div className="border-t border-gray-100 pt-4">
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  Date of Birth <span className="text-red-500">*</span>
                  <span className="ml-2 normal-case font-normal text-[10px] text-gray-500">(needed for DPDP minor check)</span>
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="date"
                    value={manualForm.dob}
                    onChange={e => setManualForm(p => ({ ...p, dob: e.target.value }))}
                    max={new Date().toISOString().slice(0, 10)}
                    data-testid="manual-dob"
                    className="h-10 rounded-lg text-sm max-w-[200px]"
                  />
                  {studentAge !== null && (
                    <span className={`text-[12px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${
                      studentAge < 18 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`} data-testid="manual-age-chip">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      {studentAge} years · {studentAge < 18 ? 'minor — parental consent required' : 'adult — no consent gate'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── DPDP — Parent / Guardian block (only when student & under 18) ── */}
            {isMinorPath && (
              <div className="mt-3 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/50 p-4" data-testid="manual-parent-block">
                <div className="flex items-start gap-2.5 mb-3">
                  <ShieldCheck className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-amber-900">Parent / Guardian — DPDP Act 2023</p>
                    <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed break-words">
                      Section 9 of India's DPDP Act requires <strong>verifiable parental consent</strong> before
                      we activate any account for a child under 18. We'll send the parent a single link they can
                      use to verify identity (Aadhaar / DigiLocker / Offline upload) and approve.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">Parent Name *</label>
                    <Input value={manualForm.parent_name} onChange={e => setManualForm(p => ({ ...p, parent_name: e.target.value }))}
                           placeholder="Rajesh Sharma" data-testid="manual-parent-name"
                           className="h-9 rounded-lg text-sm bg-white w-full" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">Relationship *</label>
                    <select value={manualForm.parent_relationship}
                            onChange={e => setManualForm(p => ({ ...p, parent_relationship: e.target.value }))}
                            data-testid="manual-parent-relationship"
                            className="w-full h-9 rounded-lg border border-gray-200 text-sm px-3 bg-white text-gray-700">
                      <option value="parent">Parent</option>
                      <option value="guardian">Legal Guardian</option>
                      <option value="grandparent">Grandparent</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div className="min-w-0">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">Parent Email *</label>
                    <Input type="email" value={manualForm.parent_email}
                           onChange={e => setManualForm(p => ({ ...p, parent_email: e.target.value }))}
                           placeholder="parent@example.com" data-testid="manual-parent-email"
                           className="h-9 rounded-lg text-sm bg-white w-full" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">Parent Mobile *</label>
                    <Input value={manualForm.parent_mobile}
                           onChange={e => setManualForm(p => ({ ...p, parent_mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                           placeholder="9876543210" data-testid="manual-parent-mobile"
                           className="h-9 rounded-lg text-sm bg-white font-mono w-full"
                           maxLength={10} />
                  </div>
                </div>

                <div className="mt-3 min-w-0">
                  <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                    WhatsApp number <span className="font-normal normal-case text-gray-500">(optional — defaults to mobile)</span>
                  </label>
                  <Input value={manualForm.parent_whatsapp}
                         onChange={e => setManualForm(p => ({ ...p, parent_whatsapp: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                         placeholder="9876543210" data-testid="manual-parent-whatsapp"
                         className="h-9 rounded-lg text-sm bg-white font-mono w-full sm:max-w-[240px]"
                         maxLength={10} />
                </div>

                <p className="text-[11px] text-amber-800 mt-3 leading-relaxed flex items-start gap-1.5 break-words">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>Account is NOT created until the parent approves. They get the link by email + WhatsApp + SMS,
                  and the consent is logged with timestamps + IP for legal audit.</span>
                </p>
              </div>
            )}

            <Button type="submit" disabled={manualSubmitting} className="h-10 px-6 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="manual-submit-btn">
              <UserPlus className="w-4 h-4 mr-1.5" />
              {manualSubmitting ? 'Processing...' : (isMinorPath ? 'Send parent consent link' : 'Add User')}
            </Button>

            {/* DPDP minor success card */}
            {manualResult?.status === 'consent_invited' && (
              <div className="mt-3 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="dpdp-consent-sent-card">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"/>
                  <div className="flex-1 text-sm text-amber-900">
                    <p className="font-bold">Parent consent link dispatched</p>
                    <p className="text-xs mt-1 opacity-90">{manualResult.message}</p>
                    <div className="text-[11px] mt-2 flex flex-wrap gap-2">
                      <ChannelBadge label="Email" mocked={!manualResult.channels?.email_real}/>
                      <ChannelBadge label="WhatsApp" mocked={!!manualResult.channels?.whatsapp_mocked}/>
                      <ChannelBadge label="SMS" mocked={!!manualResult.channels?.sms_mocked}/>
                    </div>
                    {manualResult.consent_link && (
                      <div className="mt-3 p-2 rounded bg-white border border-amber-100 text-[11px] break-all">
                        <strong>Manual share link:</strong><br/>
                        <a href={manualResult.consent_link} className="text-amber-700 hover:underline font-mono" target="_blank" rel="noreferrer">{manualResult.consent_link}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Smart result card */}
            {manualResult && manualResult.status !== 'consent_invited' && (
              <div className={`mt-3 p-3.5 rounded-lg flex items-start gap-2.5 text-sm border ${
                manualResult.status === 'user_created' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                manualResult.status === 'join_request_sent' ? 'bg-blue-50 border-blue-200 text-blue-800' :
                manualResult.status === 'already_member' ? 'bg-gray-50 border-gray-200 text-gray-700' :
                manualResult.status === 'already_pending' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                'bg-red-50 border-red-200 text-red-700'
              }`} data-testid="manual-result">
                {manualResult.status === 'user_created' && <><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" /><div><p className="font-bold">{manualResult.message}</p><p className="text-xs mt-1 opacity-80">Default password: <code className="font-mono bg-emerald-100 px-1.5 py-0.5 rounded">{manualResult.default_password}</code>{manualResult.email_sent && ' — invite email sent'}</p></div></>}
                {manualResult.status === 'join_request_sent' && <><Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" /><div><p className="font-bold">{manualResult.message}</p></div></>}
                {manualResult.status === 'already_member' && <><Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" /><span>{manualResult.message}</span></>}
                {manualResult.status === 'already_pending' && <><Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" /><span>{manualResult.message}</span></>}
              </div>
            )}

            {/* Info box */}
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-100 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-600 space-y-1">
                <p><strong>New email?</strong> Account created instantly with licence applied. They receive a welcome email with login credentials.</p>
                <p><strong>Existing Desky user?</strong> They receive a join request with your licence offer. They accept from their dashboard.</p>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ─── CSV IMPORT ─── */}
      {activeTab === 'csv' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6" data-testid="csv-import-form">
          <div className="mb-5">
            <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Import from CSV or Excel</h3>
            <p className="text-sm text-gray-500 mt-0.5">Upload a CSV or Excel (.xlsx) file with columns: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">full_name, email, phone</code>. Each new user gets a random password — download the file afterwards to get them.</p>
          </div>

          <div className="space-y-4 max-w-2xl">
            <label
              onDragOver={e => { e.preventDefault(); setCsvDragOver(true); }}
              onDragLeave={() => setCsvDragOver(false)}
              onDrop={e => { e.preventDefault(); setCsvDragOver(false); loadCsvFile(e.dataTransfer.files?.[0]); }}
              className={`flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${csvDragOver ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
              data-testid="csv-dropzone">
              <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
                onChange={e => { loadCsvFile(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="csv-file-input" />
              <FileSpreadsheet className="w-8 h-8 text-gray-400" />
              {csvFileName ? (
                <>
                  <p className="text-sm font-semibold text-gray-800">{csvFileName}</p>
                  <p className="text-xs text-gray-400">Click or drop another file to replace</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-700">Drop your CSV or Excel file here, or click to browse</p>
                  <p className="text-xs text-gray-400">.csv or .xlsx · up to 5 MB</p>
                </>
              )}
            </label>
            {csvFileName && (
              <button type="button" onClick={clearCsvFile} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="csv-clear">Remove file</button>
            )}

            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Role</label>
                <select value={csvRole} onChange={e => setCsvRole(e.target.value)} className="h-10 rounded-lg border border-gray-200 text-sm px-3 bg-white text-gray-700" data-testid="csv-role">
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  Licence
                  <span className="ml-1 normal-case font-normal text-[10px] text-gray-400">(co-terminus with institution)</span>
                </label>
                <div className="h-10 rounded-lg border border-gray-200 bg-gray-50 text-sm px-3 flex items-center text-gray-600 font-medium" data-testid="csv-duration-locked">
                  Until institution renewal
                </div>
              </div>
              <Button onClick={handleCSVImport} disabled={csvSubmitting || !hasImportFile} className="h-10 px-6 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="csv-import-btn">
                <Upload className="w-4 h-4 mr-1.5" />
                {csvSubmitting ? 'Importing...' : 'Import'}
              </Button>
            </div>

            {csvResult && (
              <div className="mt-4 space-y-2" data-testid="csv-result">
                {csvResult.added?.length > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                    <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {csvResult.added.length} user(s) added</p>
                    <p className="text-xs text-emerald-600 mt-1">Each user has their own password. Download the file now — the passwords are not shown again.</p>
                  </div>
                )}
                {(csvResult.result_xlsx_base64 || csvResult.result_csv) && (
                  <Button onClick={downloadCsvResult} variant="outline" className="h-9 rounded-lg text-sm font-semibold" data-testid="csv-download-result">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Download {csvResult.result_xlsx_base64 ? 'Excel' : 'CSV'} with passwords
                  </Button>
                )}
                {csvResult.skipped?.length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> {csvResult.skipped.length} skipped</p>
                    {csvResult.skipped.map((s, i) => <p key={i} className="text-xs text-amber-600 mt-0.5">{s.email} — {s.reason}</p>)}
                  </div>
                )}
                {csvResult.errors?.length > 0 && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                    <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> {csvResult.errors.length} error(s)</p>
                    {csvResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600 mt-0.5">{e.email} — {e.reason}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── EMAIL INVITE ─── */}
      {/* ─── SLIDE PANELS ─── */}
      <SlidePanel
        open={importsPanel}
        onClose={() => setImportsPanel(false)}
        title="Recently Added Users"
        subtitle="All users imported via CSV or added manually"
        icon={Users}
        count={recentImports.length}
      >
        <RecentImportsPanel data={recentImports} accent={A} />
      </SlidePanel>

      <SlidePanel
        open={invitesPanel}
        onClose={() => setInvitesPanel(false)}
        title="Join Requests & Invites"
        subtitle="Track join requests and signup invitations"
        icon={Send}
        count={joinRequests.length + invites.length}
      >
        {joinRequests.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 px-1">Join Requests</h4>
            <div className="space-y-1.5">
              {joinRequests.map(jr => (
                <div key={jr.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50/80 border border-gray-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{jr.full_name || jr.email}</p>
                    <p className="text-[10px] text-gray-500">{jr.email} &middot; {jr.requested_role}</p>
                  </div>
                  {jr.status === 'pending' ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleApproveJoinRequest(jr.id, jr.source)}
                        className="w-7 h-7 rounded-lg bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center transition-colors"
                        title="Approve"
                        data-testid={`approve-jr-${jr.id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      </button>
                      <button
                        onClick={() => handleRejectJoinRequest(jr.id, jr.source)}
                        className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                        title="Reject"
                        data-testid={`reject-jr-${jr.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 text-red-600" />
                      </button>
                    </div>
                  ) : (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${jr.status === 'accepted' || jr.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>{jr.status}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <InvitesPanel data={invites} />
      </SlidePanel>

      {reassignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reassign-extra-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setReassignTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Reassign extra licence</h3>
            <p className="text-xs text-gray-500 mb-4">Hand this seat to a different person. {reassignTarget.full_name}'s licence will be deactivated; the accrued charge carries over — no new charge is created.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700">New person's name</label>
                <Input value={reassignForm.full_name} onChange={(e) => setReassignForm((f) => ({ ...f, full_name: e.target.value }))} className="mt-1" data-testid="reassign-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">New person's email</label>
                <Input type="email" value={reassignForm.email} onChange={(e) => setReassignForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="reassign-email-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setReassignTarget(null)} className="flex-1 h-10 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">Cancel</button>
              <Button onClick={handleReassignExtra} disabled={reassigning} className="flex-1 h-10 rounded-xl text-sm font-semibold text-white" style={{ background: A }} data-testid="reassign-submit-btn">
                {reassigning ? 'Reassigning…' : 'Reassign'}
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
    </div>
  );
}
